// HeroKids — remembr kids edition opening hero.
// Soft baby-blue + pink, parents-to-be hook, perfect-baby-shower-gift framing.
// Mirrors Hero.jsx structure so the rest of the landing flows the same.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { TeddyIcon } from "../TeddyIcon";
import { ArrowRight } from "@phosphor-icons/react";

export function HeroKids() {
    const { t } = useTranslation();
    return (
        <section
            className="relative min-h-screen flex items-center justify-center px-6 pt-80 pb-16 bg-gradient-to-br from-[#FBF1F2] via-[#FDFBF7] to-[#EAF2F9]"
            data-testid="hero-kids-section"
        >
            {/* Soft floating petals + teddies decor */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[15%] left-[8%] opacity-40 animate-float-gentle">
                    <ForgetMeNotIcon size={48} />
                </div>
                <div className="absolute top-[22%] right-[10%] opacity-35 animate-float-gentle" style={{ animationDelay: "1.2s" }}>
                    <TeddyIcon size={42} color="#A85D62" />
                </div>
                <div className="absolute bottom-[24%] left-[14%] opacity-30 animate-float-gentle" style={{ animationDelay: "2.4s" }}>
                    <TeddyIcon size={38} color="#3F6C99" />
                </div>
                <div className="absolute bottom-[18%] right-[12%] opacity-40 animate-float-gentle" style={{ animationDelay: "0.8s" }}>
                    <ForgetMeNotIcon size={52} />
                </div>
            </div>

            <div className="relative z-10 max-w-5xl mx-auto text-center">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-sans text-xs uppercase tracking-[0.3em] text-[#A85D62] font-semibold mb-8"
                    data-testid="hero-kids-eyebrow"
                >
                    {t("hero_kids.eyebrow")}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.0, delay: 0.25 }}
                    className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight"
                    data-testid="hero-kids-headline"
                >
                    {t("hero_kids.headline")}
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.55 }}
                    className="mt-7 max-w-2xl mx-auto text-lg sm:text-xl text-[#5C5A59] font-light leading-relaxed"
                    data-testid="hero-kids-subhead"
                >
                    {t("hero_kids.subhead_part1")}
                    <span className="block mt-3 italic text-[#A85D62]">{t("hero_kids.subhead_tagline")}</span>
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.75 }}
                    className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-[#2D2B2A] font-serif italic leading-relaxed"
                    data-testid="hero-kids-couples-line"
                >
                    {t("hero_kids.couples_line")}
                </motion.p>

                {/* "Hello, let's reminisce" — kids variant */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 28 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-14 mx-auto max-w-md rounded-[2rem] p-8 sm:p-10 bg-white shadow-rem-card border border-[#F4C7CC]/40"
                    data-testid="hero-kids-greeting-card"
                >
                    <div className="flex justify-center mb-5 gap-2">
                        <TeddyIcon size={36} color="#3F6C99" />
                        <ForgetMeNotIcon size={40} />
                        <TeddyIcon size={36} color="#A85D62" />
                    </div>
                    <p className="font-serif text-2xl text-[#2D2B2A] leading-snug">{t("hero_kids.greeting_morning")}</p>
                    <p className="font-serif text-4xl text-[#A85D62] italic mt-1 leading-tight">{t("hero_kids.greeting_invite")}</p>
                    <Link
                        to="/sign-up"
                        className="inline-flex items-center gap-2 mt-8 px-9 py-3.5 rounded-full bg-[#A85D62] hover:bg-[#8C474C] text-white font-medium text-base transition-all hover:-translate-y-0.5 shadow-rem-soft"
                        data-testid="hero-kids-start-button"
                    >
                        {t("hero_kids.cta_start")}
                        <ArrowRight weight="bold" size={18} />
                    </Link>
                    <div className="mt-5">
                        <Link
                            to="/demo/ella"
                            className="text-xs uppercase tracking-[0.22em] text-[#A85D62] hover:text-[#8C474C] font-semibold border-b border-[#A85D62]/30 hover:border-[#8C474C] pb-0.5 transition-colors"
                            data-testid="hero-kids-watch-demo"
                        >
                            {t("hero_kids.cta_watch_demo")}
                        </Link>
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.0, delay: 1.4 }}
                    className="mt-10 text-sm text-[#8A8786]"
                    data-testid="hero-kids-footnote"
                >
                    {t("hero_kids.footnote")}
                </motion.p>
            </div>
        </section>
    );
}
